export const CRLF = '\r\n';
export const MAX_VCARD_LINE_OCTETS = 75;
export const QR_ERROR_CORRECTION_LEVEL = 'M';

const QR_BYTE_CAPACITY_M = [
  0, 14, 26, 42, 62, 84, 106, 122, 152, 180, 213,
  251, 287, 331, 362, 412, 450, 504, 560, 624, 666,
  711, 779, 857, 911, 997, 1059, 1125, 1190, 1264,
  1370, 1452, 1538, 1628, 1722, 1809, 1911, 1989,
  2099, 2213, 2331,
];

const clean = (value) => {
  if (value == null) return '';
  return String(value).trim().split(/\s+/).filter(Boolean).join(' ');
};

const cleanPreservingNewlines = (value) => {
  if (value == null) return '';
  return String(value).trim().replace(/\r\n/g, '\n').replace(/\r/g, '\n');
};

export const utf8ByteLength = (value) => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  return unescape(encodeURIComponent(value)).length;
};

export const escapeVCardText = (value) => (
  cleanPreservingNewlines(value)
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
);

export const splitFullName = (fullName) => {
  const tokens = clean(fullName).split(' ').filter(Boolean);
  if (tokens.length === 0) {
    return { family: '', given: '', additional: '', prefix: '', suffix: '' };
  }
  if (tokens.length === 1) {
    return { family: '', given: tokens[0], additional: '', prefix: '', suffix: '' };
  }
  if (tokens.length === 2) {
    return { family: tokens[1], given: tokens[0], additional: '', prefix: '', suffix: '' };
  }
  if (tokens.length === 3) {
    return { family: tokens[2], given: tokens.slice(0, 2).join(' '), additional: '', prefix: '', suffix: '' };
  }
  return {
    family: tokens.slice(-2).join(' '),
    given: tokens.slice(0, -2).join(' '),
    additional: '',
    prefix: '',
    suffix: '',
  };
};

export const buildStructuredName = (vcard = {}) => {
  let family = clean(vcard.family_name || vcard.last_name);
  let given = clean(vcard.given_name || vcard.first_name);
  let additional = clean(vcard.additional_names || vcard.middle_name);
  let prefix = clean(vcard.honorific_prefix || vcard.prefix);
  let suffix = clean(vcard.honorific_suffix || vcard.suffix);

  if (![family, given, additional, prefix, suffix].some(Boolean)) {
    const inferred = splitFullName(vcard.full_name || '');
    family = inferred.family;
    given = inferred.given;
    additional = inferred.additional;
    prefix = inferred.prefix;
    suffix = inferred.suffix;
  }

  return [family, given, additional, prefix, suffix];
};

const addressComponents = (vcard = {}) => [
  clean(vcard.po_box),
  clean(vcard.extended_address),
  clean(vcard.street || vcard.address),
  clean(vcard.locality || vcard.city),
  clean(vcard.region || vcard.state || vcard.province),
  clean(vcard.postal_code || vcard.zip),
  clean(vcard.country),
];

const websiteValue = (vcard = {}) => {
  const social = vcard.social_links || {};
  return clean(vcard.website || vcard.url || social.website);
};

const formatRev = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{8}T\d{6}Z$/.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
};

export const foldVCardLine = (line) => {
  const chunks = [];
  let current = '';
  let currentOctets = 0;
  let limit = MAX_VCARD_LINE_OCTETS;

  Array.from(line).forEach((char) => {
    const charOctets = utf8ByteLength(char);
    if (current && currentOctets + charOctets > limit) {
      chunks.push(current);
      current = char;
      currentOctets = charOctets;
      limit = MAX_VCARD_LINE_OCTETS - 1;
    } else {
      current += char;
      currentOctets += charOctets;
    }
  });

  chunks.push(current);
  return chunks.join(`${CRLF} `);
};

export const unfoldVCard = (payload) => (
  payload.replace(/\r\n[ \t]/g, '')
);

const cleanBase64 = (value) => {
  if (value == null) return '';
  return String(value).replace(/\s+/g, '');
};

export const buildVCardLines = (vcard = {}, { includeRev = true, photoBase64 = null } = {}) => {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
  const fullName = clean(vcard.full_name);
  const nameComponents = buildStructuredName(vcard).map(escapeVCardText);

  if (fullName || nameComponents.some(Boolean)) {
    lines.push(`N:${nameComponents.join(';')}`);
  }
  if (fullName) {
    lines.push(`FN:${escapeVCardText(fullName)}`);
  }

  [
    ['ORG', vcard.company],
    ['TITLE', vcard.job_title || vcard.title],
    ['TEL;TYPE=CELL', vcard.phone],
    ['EMAIL', vcard.email],
  ].forEach(([propertyName, value]) => {
    const cleaned = clean(value);
    if (cleaned) {
      lines.push(`${propertyName}:${escapeVCardText(cleaned)}`);
    }
  });

  const address = addressComponents(vcard);
  if (address.some(Boolean)) {
    lines.push(`ADR;TYPE=WORK:${address.map(escapeVCardText).join(';')}`);
  }

  const website = websiteValue(vcard);
  if (website) {
    lines.push(`URL:${escapeVCardText(website)}`);
  }

  const note = cleanPreservingNewlines(vcard.notes || vcard.note);
  if (note) {
    lines.push(`NOTE:${escapeVCardText(note)}`);
  }

  const photoValue = cleanBase64(photoBase64 || vcard.offline_photo_base64 || vcard.photo_base64);
  if (photoValue) {
    lines.push(`PHOTO;ENCODING=b;TYPE=JPEG:${photoValue}`);
  }

  const rev = includeRev ? formatRev(vcard.rev || vcard.updated_at) : null;
  if (rev) {
    lines.push(`REV:${rev}`);
  }

  lines.push('END:VCARD');
  return lines;
};

export const generateVCardString = (vcard = {}, options = {}) => (
  buildVCardLines(vcard, options).map(foldVCardLine).join(CRLF)
);

export const estimateQrVersion = (byteLength) => {
  for (let version = 1; version < QR_BYTE_CAPACITY_M.length; version += 1) {
    if (byteLength <= QR_BYTE_CAPACITY_M[version]) {
      return version;
    }
  }
  return null;
};

export const classifyVCardPayload = (byteLength) => {
  if (byteLength <= 800) return 'normal';
  if (byteLength <= 1200) return 'warning';
  return 'excessive';
};

export const measureVCardPayload = (payload) => {
  const utf8Bytes = utf8ByteLength(payload);
  const version = estimateQrVersion(utf8Bytes);
  const capacity = version ? QR_BYTE_CAPACITY_M[version] : null;

  return {
    characters: payload.length,
    utf8_bytes: utf8Bytes,
    qr_error_correction: QR_ERROR_CORRECTION_LEVEL,
    estimated_qr_version: version,
    estimated_capacity_bytes: capacity,
    density: capacity ? Number((utf8Bytes / capacity).toFixed(4)) : null,
    classification: classifyVCardPayload(utf8Bytes),
  };
};
